import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import {
  isSchemaDatapointEnum,
  SchemaDatapointEnum,
} from '@rossum/api-client/schemas';
import { TriggerUrl } from '@rossum/api-client/triggers';
import { DetailDrawer, DetailDrawerProps } from '@rossum/ui';
import { IconSettings } from '@rossum/ui/icons/tabler';
import {
  CircularProgress,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { SchemaWithEtag, useSchema } from '../../business/schema/useSchema';
import { useValidationTrigger } from '../../business/triggers/useValidationTrigger';
import { getSchemaDatapoints } from '../../features/schemas/getSchemaDatapoints';
import TriageConditionsForm from './TriageConditionsForm';

// Module scope keeps the reference stable across renders.
const selectEnumDatapoints = (schema: SchemaWithEtag): SchemaDatapointEnum[] =>
  getSchemaDatapoints(schema).filter(isSchemaDatapointEnum);

type TriageConditionsDrawerProps = {
  DrawerProps: Pick<DetailDrawerProps, 'open' | 'onClose'>;
  queueUrl: Url;
  schemaUrl: Url;
  triggerUrl?: TriggerUrl;
};

const TriageConditionsDrawer = ({
  queueUrl,
  triggerUrl,
  schemaUrl,
  DrawerProps: { open, onClose },
}: TriageConditionsDrawerProps) => {
  const intl = useIntl();

  const { data: trigger, isInitialLoading: isTriggerLoading } =
    useValidationTrigger(triggerUrl);

  const { data: enumDatapoints } = useSchema(schemaUrl, {
    select: selectEnumDatapoints,
  });

  return (
    <DetailDrawer
      titleIcon={
        <SvgIcon fontSize="large">
          <IconSettings />
        </SvgIcon>
      }
      title={intl.formatMessage({
        id: `components.triageConditionsDrawer.title`,
      })}
      open={open}
      keepMounted={false}
      onClose={onClose}
    >
      <Stack spacing={2} sx={{ p: 3 }}>
        <Typography color="text.secondary">
          {intl.formatMessage({ id: 'components.triageConditionsDrawer.text' })}
        </Typography>
        {!isTriggerLoading && enumDatapoints ? (
          <TriageConditionsForm
            queueUrl={queueUrl}
            enumDatapoints={enumDatapoints}
            trigger={trigger}
            onClose={() => onClose?.({}, 'backdropClick')}
          />
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
            <CircularProgress size={48} />
          </Stack>
        )}
      </Stack>
    </DetailDrawer>
  );
};

export default TriageConditionsDrawer;
